import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import { TotalGrantProgramData, totalGrantProgramResolver } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';

import AddAnotherButton from '../buttons/AddAnotherButton';
import BrandedButton from '../buttons/BrandedButton';
import TextInputField from '../inputs/TextInputField';

interface TotalGrantProgramsProps {
  initialData?: TotalGrantProgramData;
  showDoneButton: boolean;
  showBackButton?: boolean;
  onAdd?: (data: TotalGrantProgramData) => void;
  onDone?: (data: TotalGrantProgramData) => void;
  doneLabel?: string;
}

const TotalGrantProgramForm = ({
  initialData,
  showDoneButton,
  showBackButton = false,
  onDone,
  onAdd,
  doneLabel = i18n.t('mix.done')
}: TotalGrantProgramsProps) => {
  const {
    control,
    getValues,
    handleSubmit,
    setFocus,
    reset,
    trigger,
    formState: { isValid, submitCount, errors }
  } = useForm<TotalGrantProgramData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: totalGrantProgramResolver as any
  });

  const onSubmit: SubmitHandler<TotalGrantProgramData> = async (data) => {
    if (onDone) {
      onDone(data);
    }
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <TextInputField
          name="name"
          control={control}
          label={i18n.t('sign_in.nonProfit.total_grants_programs.name_label')}
          infoSubLabel={i18n.t('sign_in.nonProfit.total_grants_programs.name_sublabel')}
          placeholder={i18n.t('sign_in.nonProfit.total_grants_programs.name_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          onSubmitEditing={() => setFocus('budget')}
        />

        <TextInputField
          name="description"
          multiline
          control={control}
          label={i18n.t('sign_in.nonProfit.programs.description_label')}
          infoSubLabel={i18n.t('sign_in.nonProfit.programs.description_sublabel')}
          placeholder={i18n.t('sign_in.nonProfit.programs.description_placeholder')}
          maxHeight={80}
        />

        <TextInputField
          name="budget"
          control={control}
          label={i18n.t('sign_in.nonProfit.total_grants_programs.budget_label')}
          infoSubLabel={i18n.t('sign_in.nonProfit.total_grants_programs.budget_sublabel')}
          placeholder={i18n.t('sign_in.nonProfit.total_grants_programs.budget_placeholder')}
          type="numeric"
          currency
          returnKeyType="done"
        />

        {onAdd && (
          <AddAnotherButton
            onPress={() => {
              trigger().then((valid) => {
                if (valid) onAdd(getValues());
              });
            }}
            label={i18n.t('sign_in.nonProfit.total_grants_programs.submit_and_add')}
          />
        )}
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      {showDoneButton && (
        <View style={styles.nextBackContainer}>
          {showBackButton && router.canGoBack() && (
            <BrandedButton style={styles.flexOne} onPress={router.back}>
              {capitalize(i18n.t('mix.back'))}
            </BrandedButton>
          )}
          <BrandedButton style={styles.flexOne} onPress={handleSubmit(onSubmit)} disabled={!isValid && submitCount > 0}>
            {doneLabel}
          </BrandedButton>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    flex: 1,
    gap: 15
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default TotalGrantProgramForm;
